import type {
  OsEmailAction,
  OsEmailCopy,
  OsEmailCopyContext,
  OsEmailDealContext,
} from "./os-notification-copy-types";
import { buildNewLeadCopy } from "./os-notification-copy-new-lead";

export type * from "./os-notification-copy-types";

const DEAL_ACTIONS: OsEmailAction[] = ["snooze", "mute_deal", "not_interested"];

function pluralDays(count: number | null): string {
  if (count == null) return "a while";
  return count === 1 ? "1 day" : `${count} days`;
}

/** "Maria Lopez (Tampa HVAC)" when both are known and differ, else one of them. */
export function formatDealLabel(deal: OsEmailDealContext | null): string {
  if (!deal) return "this deal";
  const { contactName, title } = deal;
  if (contactName && title && contactName.trim() !== title.trim()) {
    return `${contactName} (${title})`;
  }
  return contactName ?? title;
}

function contactOrDeal(deal: OsEmailDealContext | null): string {
  return (
    deal?.contactFirstName ?? deal?.contactName ?? deal?.title ?? "this lead"
  );
}

/** Sentence used in the footer (warm) or at the top (cold) of every email. */
export function buildWhyLine(ctx: OsEmailCopyContext): string {
  const { partnerName } = ctx.recipient;
  if (partnerName) {
    return `You're receiving this because ${partnerName} partners with Venturu. Your Venturu OS workspace was opened through that partnership to track the leads and deals routed to you.`;
  }
  return "Venturu connects business buyers, sellers, and brokers. This email brings you activity for a lead in your broker workspace.";
}

type Builder = (ctx: OsEmailCopyContext) => Omit<OsEmailCopy, "why">;

const BUILDERS: Record<string, Builder> = {
  email_reply: (ctx) => {
    const who = ctx.event.actorName ?? contactOrDeal(ctx.deal);
    return {
      subject: `${who} replied — ${formatDealLabel(ctx.deal)}`,
      preheader: "Their reply is waiting in your deal thread.",
      headline: `${who} replied`,
      body: [
        `${who} answered your message. The reply is in the deal thread, with the history beside it.`,
      ],
      ctaLabel: "Read the reply",
      actions: ["mute_deal"],
    };
  },
  new_deal: buildNewLeadCopy,
  intake_submission: (ctx) => {
    const who = ctx.event.actorName ?? contactOrDeal(ctx.deal);
    const form = ctx.event.objectName ?? "your intake form";
    return {
      subject: `${who} completed ${form}`,
      preheader: "Their answers are ready to review.",
      headline: `${who} finished ${form}`,
      body: [
        `${who} filled out ${form}. Review the answers before you move the deal forward.`,
      ],
      ctaLabel: "Review the answers",
      actions: ctx.deal ? ["mute_deal"] : [],
    };
  },
  intake_stalled: (ctx) => {
    const who = ctx.event.actorName ?? contactOrDeal(ctx.deal);
    const form = ctx.event.objectName ?? "the intake form";
    return {
      subject: `${who} stopped midway through ${form}`,
      preheader: "A short nudge usually brings them back.",
      headline: `${who} did not finish ${form}`,
      body: [
        `${who} opened ${form} and left before the end. Their answers are saved, and a short follow-up usually finishes it.`,
      ],
      ctaLabel: `Nudge ${who}`,
      actions: ctx.deal ? DEAL_ACTIONS : ["mute_kind"],
    };
  },
  data_room_access_request: (ctx) => {
    const who = ctx.event.actorName ?? "Someone";
    const item = ctx.event.objectName ?? "your data room";
    return {
      subject: `${who} requested access to ${item}`,
      preheader: "Approve or deny in one step. They are waiting.",
      headline: `${who} is asking for ${item}`,
      body: [
        `${who} requested access to ${item}. Nothing is shared until you approve.`,
      ],
      ctaLabel: "Review the request",
      actions: [],
    };
  },
  signature_signed: (ctx) => {
    const who = ctx.event.actorName ?? contactOrDeal(ctx.deal);
    const doc = ctx.event.objectName ?? "your document";
    return {
      subject: `${who} signed ${doc}`,
      preheader: "Their signature has been recorded.",
      headline: `${who} signed`,
      body: [
        `${who} signed ${doc}. Open the document to review its signing status.`,
      ],
      ctaLabel: "View the document",
      actions: ctx.deal ? ["mute_deal"] : [],
    };
  },
  signature_expired: (ctx) => {
    const doc = ctx.event.objectName ?? "your document";
    return {
      subject: `Signature request expired: ${doc}`,
      preheader: "Resend it or follow up by phone.",
      headline: `${doc} was not signed in time`,
      body: [
        `The signing link for ${doc} expired before every party signed. Resend it, or check in with the signer first.`,
      ],
      ctaLabel: "Resend the request",
      actions: ctx.deal ? ["mute_deal"] : [],
    };
  },
  signature_declined: (ctx) => {
    const doc = ctx.event.objectName ?? "your document";
    return {
      subject: `Signature declined: ${doc}`,
      preheader: "See who declined and why before you follow up.",
      headline: `${doc} was declined`,
      body: [
        `A signer declined ${doc}. Their comment, if they left one, is on the envelope.`,
      ],
      ctaLabel: "See the decline",
      actions: ctx.deal ? ["mute_deal"] : [],
    };
  },
  valuation_failed: (ctx) => {
    const biz = ctx.event.objectName ?? ctx.deal?.title ?? "this business";
    return {
      subject: `Valuation could not be completed: ${biz}`,
      preheader: ctx.event.description ?? "Review the blocker and rerun.",
      headline: `We could not value ${biz}`,
      body: [
        ctx.event.description ??
          "The valuation stopped before producing a result. The blocker is listed on the deal.",
      ],
      ctaLabel: "Review the blocker",
      actions: ctx.deal ? ["mute_deal"] : [],
    };
  },
  research_review: (ctx) => ({
    subject: `Research needs your review: ${formatDealLabel(ctx.deal)}`,
    preheader: "Venturu AI flagged details worth a look before you proceed.",
    headline: `Check the research on ${contactOrDeal(ctx.deal)}`,
    body: [
      "Venturu AI flagged points that need a decision. Nothing is shared with the client until you approve it.",
    ],
    ctaLabel: "Review the research",
    actions: ctx.deal ? ["mute_deal"] : [],
  }),
  deal_stalled: (ctx) => {
    const days = pluralDays(ctx.deal?.idleDays ?? null);
    return {
      subject: `${formatDealLabel(ctx.deal)} — no activity in ${days}`,
      preheader: "Reply, snooze a week, or tell us it is not a fit.",
      headline: `Still working with ${contactOrDeal(ctx.deal)}?`,
      body: [
        "Reply if you are still working it, snooze it for a week, or mark it not a fit.",
      ],
      ctaLabel: "Open the deal",
      actions: DEAL_ACTIONS,
    };
  },
  buyer_prequalification_stalled: (ctx) => {
    const days = pluralDays(ctx.deal?.idleDays ?? null);
    return {
      subject: `${contactOrDeal(ctx.deal)} has not finished prequalification (${days})`,
      preheader: "A reminder from you usually gets it done.",
      headline: `${contactOrDeal(ctx.deal)} is stuck in prequalification`,
      body: [
        `${contactOrDeal(ctx.deal)} started prequalification ${days} ago and stopped. Their progress is saved.`,
      ],
      ctaLabel: "Open the buyer",
      actions: DEAL_ACTIONS,
    };
  },
  lead_lost: (ctx) => ({
    subject: `Lead rotated: ${formatDealLabel(ctx.deal)}`,
    preheader: "The inquiry moved to another broker after no response.",
    headline: `${formatDealLabel(ctx.deal)} was reassigned`,
    body: [
      ctx.event.description ??
        "This inquiry waited past the response window and was rotated to another broker.",
    ],
    ctaLabel: "See what happened",
    actions: ctx.deal ? ["mute_deal"] : [],
  }),
  campaign_failed: (ctx) => {
    const name = ctx.event.objectName ?? "your campaign";
    return {
      subject: `Campaign failed: ${name}`,
      preheader:
        ctx.event.description ?? "Nothing was sent. Review and relaunch.",
      headline: `${name} did not send`,
      body: [
        ctx.event.description ??
          "The campaign stopped before delivery. No recipient received anything.",
      ],
      ctaLabel: "Fix the campaign",
      actions: [],
    };
  },
  system_degraded: (ctx) => ({
    subject: `Venturu OS: ${ctx.event.title}`,
    preheader: ctx.event.description ?? "Part of your workspace is affected.",
    headline: ctx.event.title,
    body: [
      ctx.event.description ??
        "Part of Venturu OS is degraded. Your data is safe; some actions may be delayed.",
    ],
    ctaLabel: "See details",
    actions: [],
  }),
};

function fallbackBuilder(ctx: OsEmailCopyContext): Omit<OsEmailCopy, "why"> {
  return {
    subject: ctx.deal
      ? `${ctx.event.title}: ${formatDealLabel(ctx.deal)}`
      : ctx.event.title,
    preheader:
      ctx.event.description ?? "There is work waiting for you in Venturu OS.",
    headline: ctx.event.title,
    body: [
      ctx.event.description ?? "There is work waiting for you in Venturu OS.",
    ],
    ctaLabel: "Open Venturu OS",
    actions: ctx.deal ? ["mute_deal"] : [],
  };
}

/** Resolve the full copy for one notification email. Pure; safe to unit test. */
export function buildOsEmailCopy(ctx: OsEmailCopyContext): OsEmailCopy {
  const builder = BUILDERS[ctx.event.kind] ?? fallbackBuilder;
  const copy = builder(ctx);
  const subject = ctx.isReminder ? `Reminder: ${copy.subject}` : copy.subject;
  return {
    ...copy,
    subject,
    why: buildWhyLine(ctx),
    actions: [...new Set([...copy.actions, "mute_kind" as const])],
  };
}

export {
  buildOsDigestHeadline,
  buildOsDigestPreheader,
  buildOsDigestSubject,
  type OsDigestCopyInput,
} from "./os-notification-copy-digest";
